import React from "react";
import {
  Box,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaTrash } from "react-icons/fa";
import TruncatedTableCell from "./TruncatedTableCell";

const getEndDateStyle = (finalDate, status) => {
  if (!finalDate || status === "COMPLETED") {
    return {};
  }

  const currentDate = new Date();
  let endDate;

  if (typeof finalDate === "string") {
    endDate = new Date(finalDate);
  } else if (typeof finalDate === "number") {
    endDate = new Date(finalDate);
  } else {
    return {};
  }

  if (isNaN(endDate.getTime())) {
    return {};
  }

  const oneWeekBefore = new Date(endDate);
  oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);

  currentDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  oneWeekBefore.setHours(0, 0, 0, 0);

  if (currentDate > endDate) {
    return {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      borderLeft: "3px solid #dc2626",
      fontWeight: 500,
    };
  } else if (currentDate >= oneWeekBefore && currentDate <= endDate) {
    return {
      backgroundColor: "#fff7ed",
      color: "#ea580c",
      borderLeft: "3px solid #ea580c",
      fontWeight: 500,
    };
  }

  return {};
};

const RootCauseDisplayRow = ({
  item,
  index,
  viewOnly,
  statusNames,
  formatDateToShow,
  deleteLine,
}) => {
  return (
    <>
      <TableCell
        sx={{
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {item.attribute.attribute.referenceCode}
      </TableCell>

      <TableCell
        sx={{
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {item.attribute.attribute.name}
      </TableCell>

      <TableCell
        sx={{
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {item.serviceType || "Prestador Desconhecido"}
      </TableCell>

      <TruncatedTableCell
        content={item.description}
        title="Descrição - Detalhes"
        maxLength={80}
        sx={{
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
          minWidth: 115,
          maxWidth: 200,
        }}
      />

      <TruncatedTableCell
        content={item.cause}
        title="Causa Raiz - Detalhes"
        maxLength={50}
        sx={{
          minWidth: 175,
          maxWidth: 300,
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      />

      <TruncatedTableCell
        content={item.action}
        title="Plano de Ação - Detalhes"
        maxLength={50}
        sx={{
          minWidth: 175,
          maxWidth: 300,
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      />

      <TruncatedTableCell
        content={item.responsible}
        title="Responsável - Detalhes"
        maxLength={50}
        sx={{
          minWidth: 175,
          maxWidth: 200,
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      />

      <TableCell
        sx={{
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {formatDateToShow(item.initialDate)}
      </TableCell>

      <TableCell
        sx={{
          fontSize: "0.875rem",
          color: "#1f2937",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
          ...getEndDateStyle(item.finalDate, item.status),
        }}
      >
        {formatDateToShow(item.finalDate)}
      </TableCell>

      <TableCell
        sx={{
          fontSize: "0.875rem",
          py: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <span
          className={`
            inline-flex items-center px-2 py-1 rounded-md text-xs font-medium
            ${
              item.status === "COMPLETED"
                ? "bg-green-50 text-green-700 border border-green-200"
                : item.status === "IN_PROGRESS"
                ? "bg-blue-50 text-blue-700 border border-blue-200"
                : item.status === "NOT_STARTED"
                ? "bg-gray-50 text-gray-700 border border-gray-200"
                : item.status === "CANCELLED"
                ? "bg-red-50 text-red-700 border border-red-200"
                : item.status === "ON_HOLD"
                ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                : item.status === "UNDER_REVIEW"
                ? "bg-purple-50 text-purple-700 border border-purple-200"
                : "bg-gray-50 text-gray-700 border border-gray-200"
            }
          `}
        >
          {statusNames.find((s) => s.value === item.status).name}
        </span>
      </TableCell>

      {!viewOnly && (
        <TableCell
          sx={{
            position: "sticky",
            right: 0,
            backgroundColor: "#ffffff",
            textAlign: "center",
            borderBottom: "1px solid #f0f0f0",
            borderLeft: "2px solid #e5e7eb",
            py: 2,
            zIndex: 1,
            boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Tooltip title="Excluir item" arrow>
            <IconButton
              onClick={() => deleteLine(index)}
              sx={{
                color: "#dc2626",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
                padding: "6px",
                "&:hover": {
                  backgroundColor: "#fecaca",
                  borderColor: "#dc2626",
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <FaTrash size={12} />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
    </>
  );
};

const RootCauseEditableRow = ({ row, editableRows, handleChange }) => {
  const statusOptions = [
    { name: "Não iniciado", value: "NOT_STARTED" },
    { name: "Em andamento", value: "IN_PROGRESS" },
    { name: "Concluído", value: "COMPLETED" },
    { name: "Cancelado", value: "CANCELLED" },
    { name: "Em espera", value: "ON_HOLD" },
    { name: "Em revisão", value: "UNDER_REVIEW" },
  ];

  return (
    <TableRow>
      <TableCell style={{ padding: 0 }} colSpan={10}>
        <Box>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#fdfdfd",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "100px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Código
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "150px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Parâmetro
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "120px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Tipo Prestador
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "150px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Descrição
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "350px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Causa
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "350px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Ação
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "200px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Responsável
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "120px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Data Início
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "120px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Data Término
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1.5,
                    minWidth: "120px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Estado
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editableRows.map((item, index) => (
                <TableRow key={index} sx={{ backgroundColor: "#fafafa" }}>
                  <TableCell
                    sx={{
                      minWidth: "100px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                      {item.referenceCode}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "150px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                      {item.parameterName}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "120px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                      {item.serviceType}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "150px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                      {item.description}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "350px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <TextField
                      multiline
                      rows={3}
                      value={item.cause}
                      onChange={(e) =>
                        handleChange(index, "cause", e.target.value)
                      }
                      placeholder="Descreva a causa raiz..."
                      sx={{
                        width: "100%",
                        backgroundColor: "#fafafa",
                        borderRadius: "6px",
                        fontSize: "0.875rem",
                        "& .MuiInputBase-input": {
                          padding: "8px 12px",
                          fontSize: "0.875rem",
                        },
                        "& fieldset": { border: "1px solid #e5e7eb" },
                        "&:hover fieldset": { borderColor: "#9ca3af" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#059669",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "350px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <TextField
                      multiline
                      rows={3}
                      value={item.action}
                      onChange={(e) =>
                        handleChange(index, "action", e.target.value)
                      }
                      placeholder="Descreva o plano de ação..."
                      sx={{
                        width: "100%",
                        backgroundColor: "#fafafa",
                        borderRadius: "6px",
                        fontSize: "0.875rem",
                        "& .MuiInputBase-input": {
                          padding: "8px 12px",
                          fontSize: "0.875rem",
                        },
                        "& fieldset": { border: "1px solid #e5e7eb" },
                        "&:hover fieldset": { borderColor: "#9ca3af" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#059669",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "200px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <TextField
                      value={item.responsible}
                      onChange={(e) =>
                        handleChange(index, "responsible", e.target.value)
                      }
                      placeholder="Nome do responsável..."
                      sx={{
                        width: "100%",
                        backgroundColor: "#fafafa",
                        borderRadius: "6px",
                        fontSize: "0.875rem",
                        "& .MuiInputBase-input": {
                          padding: "8px 12px",
                          fontSize: "0.875rem",
                        },
                        "& fieldset": { border: "1px solid #e5e7eb" },
                        "&:hover fieldset": { borderColor: "#9ca3af" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#059669",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "120px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <TextField
                      type="date"
                      value={item.initialDate}
                      onChange={(e) =>
                        handleChange(index, "initialDate", e.target.value)
                      }
                      sx={{
                        width: "100%",
                        backgroundColor: "#fafafa",
                        borderRadius: "6px",
                        fontSize: "0.875rem",
                        "& .MuiInputBase-input": {
                          padding: "8px 12px",
                          fontSize: "0.875rem",
                        },
                        "& fieldset": { border: "1px solid #e5e7eb" },
                        "&:hover fieldset": { borderColor: "#9ca3af" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#059669",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "120px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <TextField
                      type="date"
                      value={item.finalDate}
                      onChange={(e) =>
                        handleChange(index, "finalDate", e.target.value)
                      }
                      sx={{
                        width: "100%",
                        backgroundColor: "#fafafa",
                        borderRadius: "6px",
                        fontSize: "0.875rem",
                        "& .MuiInputBase-input": {
                          padding: "8px 12px",
                          fontSize: "0.875rem",
                        },
                        "& fieldset": { border: "1px solid #e5e7eb" },
                        "&:hover fieldset": { borderColor: "#9ca3af" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#059669",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "120px",
                      padding: "8px 4px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Select
                      variant="outlined"
                      value={item.status}
                      onChange={(e) =>
                        handleChange(index, "status", e.target.value)
                      }
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span
                              style={{ color: "#9ca3af", fontSize: "0.875rem" }}
                            >
                              Informe um estado...
                            </span>
                          );
                        }
                        return (
                          statusOptions.find(
                            (option) => option.value === selected
                          )?.name ?? selected
                        );
                      }}
                      sx={{
                        width: "100%",
                        backgroundColor: "#fafafa",
                        borderRadius: "6px",
                        fontSize: "0.875rem",
                        "& .MuiSelect-select": { padding: "8px 12px" },
                        "& fieldset": { border: "1px solid #e5e7eb" },
                        "&:hover fieldset": { borderColor: "#9ca3af" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#059669",
                          borderWidth: "1px",
                        },
                      }}
                    >
                      <MenuItem
                        value=""
                        sx={{ fontSize: "0.875rem", color: "#9ca3af" }}
                      >
                        <em>Informe um estado...</em>
                      </MenuItem>
                      {statusOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const RootCauseTableRow = (props) => {
  if (props.editableRows && props.handleChange) {
    return <RootCauseEditableRow {...props} />;
  }

  return <RootCauseDisplayRow {...props} />;
};

export default RootCauseTableRow;
